// Monthly credits against pack credits across organizations (PLAN-offers.md §15.7): what the current periods
// allocate and spend, what is left (lost at renewal if unspent), and what packs hold, give and lose to expiry. A row is
// in its organization's currency (balances are never converted, sculpt-api PLAN-multi-currency.md §2c); the totals
// add the rows up in euros at today's rates.
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { useQueryParams } from '../../hooks/useQueryParams';
import { dateOnly, eur, money } from '../../services/format';
import { Alert, Badge, Cell, Checkbox, Field, Filters, PageTitle, Row, Select, Stat, Table } from '../../components/ui';
import type { CreditsRow } from '../../types/api';

/** Share of the monthly credits spent, 0–1; null when unlimited or none. */
function used(row: CreditsRow): number | null {
  const pool = row.pool === null ? null : Number(row.pool);
  return pool ? Math.min(1, Number(row.pool_spent) / pool) : null;
}

export function CreditsOverview() {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const navigate = useNavigate();
  const { data, error, loading } = useAsync(() => api.credits(), []);
  const { get, set, reset } = useQueryParams();
  const offer = get('offer');
  const onlyPacks = get('packs') === 'true';
  const offers = useMemo(() => [...new Set((data?.items ?? []).map((r) => r.offer_key).filter((k): k is string => k !== null))].sort(), [data]);
  const rows = useMemo(
    () =>
      (data?.items ?? [])
        .filter((r) => (!offer || r.offer_key === offer) && (!onlyPacks || Number(r.pack_spent) > 0 || Number(r.pack_balance) > 0))
        .sort((a, b) => (used(b) ?? -1) - (used(a) ?? -1) || Number(b.pack_spent) - Number(a.pack_spent)),
    [data, offer, onlyPacks],
  );
  const totals = data?.totals;
  const spentRatio = totals && Number(totals.pool) > 0 ? Number(totals.pool_spent) / Number(totals.pool) : null;

  return (
    <div>
      <PageTitle title={t('credits.title')} subtitle={t('credits.subtitle')} />
      {error && <Alert>{error}</Alert>}
      {totals && (
        <div className="mb-5 flex flex-col gap-4">
          <section>
            <h2 className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-faint">{t('credits.monthly')}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Stat label={t('credits.allocated')} value={eur(totals.pool, l)} hint={t('common.count', { count: totals.organizations })} />
              <Stat label={t('credits.spent')} value={eur(totals.pool_spent, l)} hint={spentRatio === null ? undefined : `${Math.round(spentRatio * 100)} %`} />
              <Stat label={t('credits.left')} value={eur(totals.pool_left, l)} />
            </div>
          </section>
          <section>
            <h2 className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-faint">{t('credits.packs')}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <Stat label={t('credits.packSpent')} value={eur(totals.pack_spent, l)} />
              <Stat label={t('credits.packBalance')} value={eur(totals.pack_balance, l)} />
              <Stat label={t('credits.packExpired')} value={eur(totals.pack_expired, l)} tone={Number(totals.pack_expired) > 0 ? 'warn' : undefined} />
              <Stat label={t('credits.packsSold')} value={eur(totals.packs_sold, l)} />
              <Stat label={t('credits.packsGiven')} value={eur(totals.packs_given, l)} />
            </div>
          </section>
          <p className="text-[12px] text-faint">{t('credits.note')}</p>
          <p className="text-[12px] text-faint" data-testid="credits-in-currency">{t('credits.inCurrency')}</p>
        </div>
      )}
      <Filters onReset={reset}>
        <Field label={t('credits.filterOffer')}>
          <Select value={offer} onChange={(e) => set({ offer: e.target.value })} data-testid="credits-offer">
            <option value="">{t('common.any')}</option>
            {offers.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Select>
        </Field>
        <div className="self-end pb-1.5">
          <Checkbox label={t('credits.onlyPacks')} checked={onlyPacks} onChange={(e) => set({ packs: e.target.checked ? 'true' : undefined })} />
        </div>
      </Filters>
      <Table
        head={[t('credits.organization'), t('credits.offer'), t('credits.monthlyTotal'), t('credits.monthlyUse'), t('credits.packSpent'), t('credits.packBalance'), t('credits.nextExpiry'), t('credits.packExpired')]}
        empty={loading ? t('common.loading') : t('common.empty')}
      >
        {rows.map((r) => {
          const ratio = used(r);
          return (
            <Row key={r.organization_id} onClick={() => navigate(`/organizations/${r.organization_id}?tab=subscription`)}>
              <Cell>{r.organization_name}</Cell>
              <Cell>{r.offer_name ?? <span className="text-faint">{t('credits.noOffer')}</span>}</Cell>
              <Cell mono>{r.offer_key ? money(r.monthly_total, r.currency, l) : '—'}</Cell>
              <Cell mono className="min-w-[200px]">
                {r.offer_key === null ? (
                  '—'
                ) : r.pool === null ? (
                  `${money(r.pool_spent, r.currency, l)} · ${t('credits.unlimited')}`
                ) : (
                  <div className="flex flex-col gap-1">
                    <span>
                      {money(r.pool_spent, r.currency, l)} / {money(r.pool, r.currency, l)} {ratio !== null && ratio >= 1 && <Badge tone="bad">{t('credits.exhausted')}</Badge>}
                      {r.pool_prorated_since && r.pool_full !== null && (
                        <span title={t('credits.proratedHint', { date: dateOnly(r.pool_prorated_since, l), full: money(r.pool_full, r.currency, l) })}>
                          {' '}
                          <Badge tone="warn">{t('credits.prorated')}</Badge>
                        </span>
                      )}
                    </span>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-hover" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round((ratio ?? 0) * 100)}>
                      <div className={`h-full rounded-full ${(ratio ?? 0) >= 1 ? 'bg-danger' : (ratio ?? 0) > 0.8 ? 'bg-warn' : 'bg-accent'}`} style={{ width: `${(ratio ?? 0) * 100}%` }} />
                    </div>
                  </div>
                )}
              </Cell>
              <Cell mono>{Number(r.pack_spent) > 0 ? money(r.pack_spent, r.currency, l) : '—'}</Cell>
              <Cell mono>{Number(r.pack_balance) > 0 ? money(r.pack_balance, r.currency, l) : '—'}</Cell>
              <Cell mono>{r.pack_next_expiry ? dateOnly(r.pack_next_expiry, l) : '—'}</Cell>
              <Cell mono>{Number(r.pack_expired) > 0 ? money(r.pack_expired, r.currency, l) : '—'}</Cell>
            </Row>
          );
        })}
      </Table>
    </div>
  );
}
